import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import {
  useListMembers,
  useListCheckins,
  useCreateCheckin,
  getListCheckinsQueryKey,
  getGetSquadSummaryQueryKey,
  getListMembersQueryKey,
  useGetSquad,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "wouter";
import { localDateStr } from "@/lib/localDate";
import { useCurrentMember } from "@/hooks/useCurrentMember";

export default function Checkin({ squadId }: { squadId: number }) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { data: squad, isError: squadError } = useGetSquad(squadId, {
    query: { enabled: !!squadId, queryKey: [["squads", squadId]] },
  });
  const { data: members, isError: membersError } = useListMembers(
    { squadId },
    { query: { enabled: !!squadId, queryKey: getListMembersQueryKey({ squadId }) } }
  );
  const createCheckin = useCreateCheckin();
  const { data: todayCheckins } = useListCheckins(
    { squadId },
    { query: { enabled: !!squadId, queryKey: getListCheckinsQueryKey({ squadId }) } }
  );

  const today = localDateStr();

  const alreadyCheckedInToday = new Set(
    (todayCheckins ?? [])
      .filter((c) => c.checkinDate === today)
      .map((c) => c.memberId)
  );

  const [memberId, setMemberId] = useState<number | null>(null);
  // WI#23 — a runner checks in only as themselves; the server already refuses anything else
  // (403). Only a captain or admin gets the "Who's checking in?" picker.
  const { member: me, canManageSquad } = useCurrentMember(squadId);
  const selfOnly = !canManageSquad && !!me;
  useEffect(() => {
    if (selfOnly && me) setMemberId(me.id);
  }, [selfOnly, me]);
  const [status, setStatus] = useState<"crushed" | "skipped" | null>(null);
  const [note, setNote] = useState("");
  const [success, setSuccess] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);
  const [genericError, setGenericError] = useState(false);

  const selectedAlreadyLogged = memberId !== null && alreadyCheckedInToday.has(memberId);

  if (squadError || membersError) {
    return (
      <div className="max-w-xl mx-auto flex flex-col items-center justify-center h-64 gap-3">
        <div className="text-destructive font-mono text-sm font-medium">Unable to load check-in</div>
        <div className="text-muted-foreground text-xs text-center max-w-xs">
          There was a problem fetching your data. Check your connection or try refreshing the page.
        </div>
      </div>
    );
  }

  const handleSubmit = () => {
    if (!memberId || !status) return;
    setDuplicateError(false);
    setGenericError(false);
    createCheckin.mutate(
      {
        data: {
          memberId,
          squadId,
          status,
          note: note.trim() || null,
          checkinDate: today,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCheckinsQueryKey({ squadId }) });
          queryClient.invalidateQueries({ queryKey: getGetSquadSummaryQueryKey(squadId) });
          queryClient.invalidateQueries({ queryKey: getListMembersQueryKey({ squadId }) });
          setSuccess(true);
          setTimeout(() => {
            navigate(`/squads/${squadId}`);
          }, 2000);
        },
        onError: (error) => {
          if ("status" in error && error.status === 409) {
            setDuplicateError(true);
          } else {
            setGenericError(true);
          }
        },
      }
    );
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center gap-2 mb-8">
        <Link href={`/squads/${squadId}`} className="text-muted-foreground text-sm hover:text-foreground transition-colors">
          {squad?.name ?? "Squad"}
        </Link>
        <span className="text-muted-foreground text-sm">/</span>
        <span className="text-sm">Check-in</span>
      </div>

      <AnimatePresence mode="wait">
        {success ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="border-2 border-primary bg-primary rounded p-12 text-center"
          >
            <div className="font-display text-5xl font-bold text-primary-foreground mb-3">
              {status === "crushed" ? "CRUSHED IT" : "LOGGED"}
            </div>
            <p className="text-primary-foreground/80 text-sm">
              {status === "crushed"
                ? "Your squad sees the work."
                : "Honest. That counts too."}
            </p>
            <p className="text-primary-foreground/60 text-xs font-mono mt-3">Returning to squad...</p>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div>
              <h1 className="font-display text-3xl font-bold mb-1">Log Today's Check-in</h1>
              <p className="text-muted-foreground text-sm font-mono">{today}</p>
            </div>

            {selfOnly && me ? (
              <div>
                <label className="pr-label">Checking in as</label>
                <div className="font-display text-lg font-bold">
                  {me.name}
                  <span className="ml-2 text-xs font-mono font-normal text-muted-foreground">
                    {alreadyCheckedInToday.has(me.id) ? "already logged today" : `${me.currentStreak}d streak`}
                  </span>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Who's checking in?
                </label>
                <select
                  value={memberId ?? ""}
                  onChange={(e) => {
                    const id = Number(e.target.value) || null;
                    setMemberId(id);
                    setDuplicateError(false);
                    setGenericError(false);
                  }}
                  className="pr-field"
                >
                  <option value="">Select runner...</option>
                  {members?.map((m) => {
                    const logged = alreadyCheckedInToday.has(m.id);
                    return (
                      <option key={m.id} value={m.id}>
                        {m.name} — {logged ? "already logged" : `${m.currentStreak}d streak`}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">
                How'd it go?
              </label>
              <div className="grid grid-cols-2 gap-3">
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setStatus("crushed")}
                  className={`relative border-2 rounded p-6 text-left transition-all ${
                    status === "crushed"
                      ? "border-primary bg-primary"
                      : "border-border hover:border-primary/40"
                  }`}
                >
                  <div className={`font-display text-2xl font-bold mb-1 ${status === "crushed" ? "text-primary-foreground" : "text-foreground"}`}>CRUSHED IT</div>
                  <div className="text-muted-foreground text-sm">Showed up. Did the work.</div>
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setStatus("skipped")}
                  className={`relative border-2 rounded p-6 text-left transition-all ${
                    status === "skipped"
                      ? "border-muted-foreground bg-secondary/50"
                      : "border-border hover:border-muted-foreground/40"
                  }`}
                >
                  {status === "skipped" && (
                    <motion.div
                      layoutId="status-indicator"
                      className="absolute inset-0 border-2 border-muted-foreground rounded"
                    />
                  )}
                  <div className="font-display text-2xl font-bold text-muted-foreground mb-1">SKIPPED</div>
                  <div className="text-muted-foreground text-sm">Honest. Your squad sees this.</div>
                </motion.button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
                Note (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Felt strong / Legs needed rest / Travel day..."
                rows={2}
                className="pr-field"
              />
            </div>

            {(duplicateError || selectedAlreadyLogged) && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-yellow-500/40 bg-yellow-500/10 rounded px-4 py-3 text-sm text-yellow-300"
              >
                <span className="font-semibold">Already checked in today.</span>{" "}
                This runner's status for {today} has already been recorded.
              </motion.div>
            )}

            {genericError && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-destructive/40 bg-destructive/10 rounded px-4 py-3 text-sm text-destructive-foreground"
              >
                <span className="font-semibold">Something went wrong.</span>{" "}
                Please try again.
              </motion.div>
            )}

            <button
              onClick={handleSubmit}
              disabled={!memberId || !status || createCheckin.isPending || selectedAlreadyLogged}
              className="w-full bg-primary text-primary-foreground py-3 rounded font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {createCheckin.isPending ? "Logging..." : "Log Check-in"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
